import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, TextField, InputAdornment,
  Select, MenuItem, Box, Chip, Typography, Button, Stack,
} from '@mui/material';
import { Search, AdminPanelSettings, Refresh, CloudUpload, Groups, Dns, Schedule } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAnalysis } from '../AnalysisContext';
import { PageHeader, EmptyState, KpiTile, NameAvatar, GRADIENTS, formatDateTime } from '../components/ui';

// Admin-only: every customer upload (moved off the main page, 2026-10 redesign). Same data as before (/api/admin/analyses).
const userLabel = (a) => (a.userEmail && a.userEmail !== 'unknown') ? a.userEmail : (a.userId !== 'anonymous' ? (a.userId || '').substring(0, 8) + '…' : 'anonymous');

export default function Admin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, adminData, loadingLists, loadAdminUpload, refresh } = useAnalysis();
  const [query, setQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [range, setRange] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [opening, setOpening] = useState(null);

  const users = useMemo(() => [...new Set(adminData.map(userLabel))].sort(), [adminData]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cutoff = range === 'all' ? 0 : Date.now() - parseInt(range, 10) * 86400000;
    return adminData.filter(a =>
      (userFilter === 'all' || userLabel(a) === userFilter) &&
      (!cutoff || new Date(a.uploadDate || 0).getTime() >= cutoff) &&
      (!q || `${a.customerName || ''} ${userLabel(a)} ${(a.sharedWith || []).join(' ')}`.toLowerCase().includes(q)));
  }, [adminData, query, userFilter, range]);
  const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!loadingLists && !isAdmin) return <Navigate to="/" replace />;

  const live = adminData.filter(a => !a.deleted);
  const open = async (a) => { if (a.deleted) return; setOpening(a.uploadId); const ok = await loadAdminUpload(a); setOpening(null); if (ok) navigate('/'); };

  return (
    <>
      <PageHeader
        title={t('nav.customerUploads', 'Customer uploads')}
        subtitle={t('admin.subtitle', 'Every analysis uploaded by any user. Click a row to open it. Administrators only.')}
        actions={<Button startIcon={<Refresh />} onClick={refresh}>{t('admin.refresh', 'Refresh')}</Button>}
      />

      {adminData.length === 0 ? (
        <EmptyState icon={<AdminPanelSettings />} title={t('admin.emptyTitle', 'No uploads yet')} text={t('admin.emptyText', 'Uploads from all users will appear here.')} />
      ) : (
        <>
          <Stack className="stagger" direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <KpiTile icon={<CloudUpload />} gradient={GRADIENTS.brand} label={t('admin.total', 'Total uploads')} value={live.length} hint={`${adminData.length - live.length} deleted by users`} />
            <KpiTile icon={<Groups />} gradient={GRADIENTS.sky} label={t('admin.users', 'Users')} value={users.length} />
            <KpiTile icon={<Dns />} gradient={GRADIENTS.emerald} label={t('admin.servers', 'Servers analysed')} value={live.reduce((s, a) => s + (a.serverCount || 0), 0)} />
            <KpiTile icon={<Schedule />} gradient={GRADIENTS.amber} label={t('admin.last', 'Latest upload')} value={live[0]?.uploadDate ? new Date(live[0].uploadDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'} />
          </Stack>

          <Paper variant="outlined">
            <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                placeholder={t('admin.search', 'Search customer, user or recipient…')}
                value={query} onChange={e => { setQuery(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: 300, flex: { xs: 1, sm: 'none' } }}
              />
              <Select size="small" value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(0); }} sx={{ minWidth: 190 }}>
                <MenuItem value="all">{t('admin.allUsers', 'All users')}</MenuItem>
                {users.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
              <Select size="small" value={range} onChange={e => { setRange(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
                <MenuItem value="all">{t('admin.anyTime', 'Any time')}</MenuItem>
                <MenuItem value="7">{t('admin.d7', 'Last 7 days')}</MenuItem>
                <MenuItem value="30">{t('admin.d30', 'Last 30 days')}</MenuItem>
                <MenuItem value="90">{t('admin.d90', 'Last 90 days')}</MenuItem>
              </Select>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>{rows.length} {t('admin.results', 'results')}</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('history.customer', 'Customer / workload')}</TableCell>
                    <TableCell>{t('admin.user', 'User')}</TableCell>
                    <TableCell align="center">{t('history.servers', 'Servers')}</TableCell>
                    <TableCell>{t('admin.sharedWith', 'Shared with')}</TableCell>
                    <TableCell>{t('history.uploaded', 'Uploaded')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((a, i) => (
                    <TableRow key={`${a.userId}-${a.uploadId}-${i}`} hover onClick={() => open(a)} style={{ animation: 'fadeIn .5s ease both', animationDelay: `${Math.min(i, 12) * 45}ms` }}
                      sx={{ cursor: a.deleted ? 'default' : 'pointer', ...(a.deleted ? { opacity: 0.55 } : {}), ...(opening === a.uploadId ? { bgcolor: 'action.hover' } : {}) }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <NameAvatar name={a.customerName} />
                          {a.customerName || '—'}
                          {a.deleted && <Chip size="small" label={t('admin.deleted', 'deleted')} sx={{ height: 20 }} />}
                        </Box>
                      </TableCell>
                      <TableCell>{userLabel(a)}</TableCell>
                      <TableCell align="center">{a.serverCount || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {a.sharedWith?.length > 0 ? a.sharedWith.map(e => <Chip key={e} size="small" variant="outlined" label={e} sx={{ mr: 0.5, mb: 0.25 }} />) : '—'}
                      </TableCell>
                      <TableCell>{formatDateTime(a.uploadDate)}</TableCell>
                    </TableRow>
                  ))}
                  {pageRows.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('admin.noMatch', 'No uploads match your filters.')}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[15, 30, 60]} />
          </Paper>
        </>
      )}
    </>
  );
}
